import type { CSSProperties, FC } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { L10nText } from '../../../services/uiTranslations/reactBindings/L10nText';
import { useModifiers } from '../../../styles/hooks/useModifiers';

import type { TrafficLightConfigType } from './types';

import styles from './TrafficLight.module.css';

export interface ITrafficLightStatus extends TrafficLightConfigType {
  calculatedScheduledStatusChange?: {
    nextStatus: string;
    timeout: number;
  };
}

interface ITrafficLightTimer {
  timer: number | null;
}

interface ITrafficLightLabel extends ITrafficLightTimer {
  config: TrafficLightConfigType;
}

export interface TrafficLightViewProps {
  animationDuration?: number;
  /**
   * Enables fallback animation with "left" instead of translateX. This is due to a bug in iOS Chrome LCE-64741
   */
  fallbackAnimation?: boolean;
  /**
   *  Pauses the timer and animation. Used in game help and storybook
   */
  isPaused?: boolean;
  /** If true traffic light will be shown as line */
  line?: boolean;
  onScheduledStatusChange: (nextStatus: string) => void;
  progressOffset?: number;
  resetAnimationDurationCallback?: () => void;
  status: ITrafficLightStatus;
  timerStartTime: number;
}

const TrafficLight = forwardRef<HTMLDivElement, TrafficLightViewProps>(function TrafficLightFunc(
  {
    animationDuration = 0,
    fallbackAnimation,
    isPaused = false,
    line,
    onScheduledStatusChange,
    progressOffset = 0,
    resetAnimationDurationCallback,
    status,
    timerStartTime,
  },
  progressbarForwardRef
) {
  const animationDurationInSeconds = useMemo(() => Math.round(animationDuration / 1_000), [animationDuration]);

  const [currentTimer, setCurrentTimer] = useState<number>(0); // in seconds

  const loopRef = useRef<number>();

  const handleTimerExpiration = useCallback((): void => {
    if (!loopRef.current) return;
    window.clearTimeout(loopRef.current);
    loopRef.current = undefined;
    resetAnimationDurationCallback?.();
  }, [resetAnimationDurationCallback]);

  const handleTimeout = useCallback((): void => {
    if (!animationDurationInSeconds) return;
    const elapsedMilliSeconds = Date.now() - timerStartTime;
    const elapsedSeconds = elapsedMilliSeconds / 1_000;
    const newTimerValue = Math.round(animationDurationInSeconds - elapsedSeconds);

    const handleTimerUpdate = (newTimer: number, elapsedSeconds: number): void => {
      setCurrentTimer(newTimer);
      const timeUntilNextSecond = (Math.ceil(elapsedSeconds) - elapsedSeconds) * 1_000;
      loopRef.current = window.setTimeout(handleTimeout, timeUntilNextSecond);
    };

    if (newTimerValue <= 0) {
      setCurrentTimer(0);
    } else {
      handleTimerUpdate(newTimerValue, elapsedSeconds);
    }
  }, [animationDurationInSeconds, timerStartTime]);

  useEffect(() => {
    if (status.rendersTimer) {
      if (!loopRef.current && animationDurationInSeconds > 0) {
        setCurrentTimer(animationDurationInSeconds);
        loopRef.current = window.setTimeout(handleTimeout, 1_000);
      }
    } else if (loopRef.current) {
      handleTimerExpiration();
      setCurrentTimer(0);
    }
    if (isPaused) {
      setCurrentTimer(animationDurationInSeconds);
      window.clearTimeout(loopRef.current);
      loopRef.current = undefined;
    }
  }, [animationDurationInSeconds, handleTimeout, handleTimerExpiration, isPaused, status.rendersTimer, timerStartTime]);

  useEffect(() => {
    const { calculatedScheduledStatusChange } = status;
    if (calculatedScheduledStatusChange && animationDuration > 0) {
      let timeout = calculatedScheduledStatusChange.timeout; // -5000
      if (timeout < 0) {
        timeout = animationDuration + timeout;
      }
      const timeoutId = window.setTimeout(() => {
        onScheduledStatusChange(calculatedScheduledStatusChange.nextStatus);
      }, timeout);
      return (): void => {
        window.clearTimeout(timeoutId);
      };
    }
  }, [status, isPaused, onScheduledStatusChange, animationDuration, timerStartTime]);

  const isAnimated = animationDuration !== 0 && status.rendersTimer;
  const isTrafficLightPaused = isPaused || (animationDuration == 0 && status.rendersTimer);

  const mods = useModifiers(styles, 'trafficLight', {
    fallbackAnimation,
    isAnimated: isAnimated,
    isPaused: isTrafficLightPaused,
    line,
  });

  return (
    <div
      data-testid="traffic-light-container"
      className={`${styles.trafficLight} ${mods}`}
      style={
        {
          '--trafficLightBackground': `var(--game-traffic-light-${status.style})`,
          '--trafficLightFontColor': `var(--game-traffic-light-color-${status.color})`,
        } as CSSProperties
      }
    >
      <div data-testid="traffic-light" className={`${styles.trafficLight__progress}`}>
        <div
          className={styles.trafficLight__progressBackground}
          ref={progressbarForwardRef}
          style={
            {
              '--animationDuration': `${Math.floor(animationDuration / 1000)}s`,
              '--progressOffset': `${progressOffset * -100}%`,
            } as CSSProperties
          }
        />
      </div>
      <div
        data-testid={status.label}
        className={styles.trafficLight__label}
        dir={status.isLTR !== undefined && status.isLTR ? 'ltr' : undefined}
      >
        <TrafficLightLabel config={status} timer={currentTimer} />
      </div>
    </div>
  );
});

const TrafficLightLabel: FC<ITrafficLightLabel> = ({ config: Config, timer }) => {
  if (Config.render) {
    return <Config.render timer={<TrafficLightTimer timer={timer} />} />;
  }

  return (
    <>
      {Config.label ? <L10nText tk={Config.label} dynamicValues={Config.attrs} /> : null}
      {Config.rendersTimer ? <TrafficLightTimer timer={timer} /> : null}
    </>
  );
};

const TrafficLightTimer: FC<ITrafficLightTimer> = ({ timer }) => {
  if (!timer) return null;

  return (
    <>
      <span className={styles.trafficLight__counterSeparator}>&nbsp;-&nbsp;</span>
      <span className={styles.trafficLight__counter}>
        <span className={styles.trafficLight__counterValue}>{timer}</span>
        {/* This 88 is a spacer element to avoid fluctating width of the countdown */}
        88
      </span>
    </>
  );
};

TrafficLight.displayName = 'TrafficLight';

export { TrafficLight };
